import { pgTable, text, serial, integer, boolean, timestamp, doublePrecision, jsonb, unique } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Employee status enum - standardized to 4 options
export const employeeStatusEnum = ["Active", "Inactive", "Onboarding", "Terminated"] as const;
export type EmployeeStatus = typeof employeeStatusEnum[number];

// Tenants table
export const tenants = pgTable("tenants", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  subdomain: text("subdomain"),
  domain: text("domain"),
  status: text("status").notNull().default("active"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  planId: integer("plan_id"),
  stripeCustomerId: text("stripe_customer_id"),
  stripeSubscriptionId: text("stripe_subscription_id"),
  subscriptionStatus: text("subscription_status"),
  trialStartsAt: timestamp("trial_starts_at"),
  trialEndsAt: timestamp("trial_ends_at"),
  dataDeletesAt: timestamp("data_deletes_at"),
  settings: jsonb("settings"),
  isVerified: boolean("is_verified").default(false),
  verificationToken: text("verification_token"),
  verificationTokenExpiry: timestamp("verification_token_expiry"),
  primaryContactEmail: text("primary_contact_email"),
});

// Users table
export const users = pgTable("users", {
  id: serial("id").primaryKey(),
  tenantId: integer("tenant_id").references(() => tenants.id, { onDelete: 'cascade' }),
  isSuperAdmin: boolean("is_super_admin").default(false),
  isOwner: boolean("is_owner").default(false),
  role: text("role").notNull().default("user"),
  avatar: text("avatar"),
  username: text("username"), // Made optional for OAuth users
  password: text("password"), // Made optional for OAuth users
  fullName: text("full_name").notNull(),
  email: text("email").notNull().unique(),
  currentTenantId: integer("current_tenant_id"),
  department: text("department"),
  
  // OAuth authentication fields
  googleId: text("google_id").unique(),
  oauthProvider: text("oauth_provider"), // 'google', 'magic_link', 'password'
  profileImageUrl: text("profile_image_url"),
  
  // Magic link authentication
  magicToken: text("magic_token"),
  magicTokenExpiry: timestamp("magic_token_expiry"),
  lastMagicLinkSent: timestamp("last_magic_link_sent"),
  
  // Email verification fields
  isEmailVerified: boolean("is_email_verified").default(false),
  emailVerificationToken: text("email_verification_token"),
  emailVerificationExpiry: timestamp("email_verification_expiry"),
  
  // Password reset fields
  passwordResetToken: text("password_reset_token"),
  passwordResetExpiry: timestamp("password_reset_expiry"),
  passwordResetUsed: boolean("password_reset_used").default(false),
  
  // Onboarding and company info
  onboardingCompleted: boolean("onboarding_completed").default(false),
  companyName: text("company_name"),
  companySize: text("company_size"), // 'startup', 'small', 'medium', 'large', 'enterprise'
  industry: text("industry"),
  jobRole: text("job_role"),
  phoneNumber: text("phone_number"),
  
  // Legal compliance
  termsAcceptedAt: timestamp("terms_accepted_at"),
  privacyAcceptedAt: timestamp("privacy_accepted_at"),
  cookieConsent: boolean("cookie_consent").default(false),
  marketingConsent: boolean("marketing_consent").default(false),
  
  // Timestamps
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// Email OTP Verification table
export const emailOtpVerification = pgTable("email_otp_verification", {
  id: serial("id").primaryKey(),
  email: text("email").notNull(),
  otp: text("otp").notNull(),
  purpose: text("purpose").notNull().default("registration"), // 'registration', 'password_reset', etc.
  createdAt: timestamp("created_at").notNull().defaultNow(),
  expiresAt: timestamp("expires_at").notNull(),
  isUsed: boolean("is_used").default(false),
  attemptCount: integer("attempt_count").default(0),
  maxAttempts: integer("max_attempts").default(3),
});

// Verification Tokens table - for email verification BEFORE user creation
export const verificationTokens = pgTable("verification_tokens", {
  id: serial("id").primaryKey(),
  email: text("email").notNull(),
  token: text("token").notNull(),
  purpose: text("purpose").notNull().default("registration"), // 'registration', 'password_reset'
  createdAt: timestamp("created_at").notNull().defaultNow(),
  expiresAt: timestamp("expires_at").notNull(),
  isUsed: boolean("is_used").default(false),
});

export type VerificationToken = typeof verificationTokens.$inferSelect;
export type InsertVerificationToken = typeof verificationTokens.$inferInsert;

// Applications table
export const applications = pgTable("applications", {
  id: serial("id").primaryKey(),
  tenantId: integer("tenant_id").references(() => tenants.id, { onDelete: 'cascade' }),
  name: text("name").notNull(),
  description: text("description"),
  category: text("category").notNull(),
  addedAt: timestamp("added_at").notNull().defaultNow(),
}, (table) => ({
  // Unique constraint: application name must be unique per tenant
  uniqueNamePerTenant: unique().on(table.name, table.tenantId),
}));

// Licenses table
export const licenses = pgTable("licenses", {
  id: serial("id").primaryKey(),
  applicationId: integer("application_id").notNull().references(() => applications.id, { onDelete: 'cascade' }),
  name: text("name").notNull(),
  totalSeats: integer("total_seats").notNull(),
  cost: doublePrecision("cost").notNull(),
  currency: text("currency").notNull().default("USD"),
  billingCycle: text("billing_cycle").notNull(),
  startDate: timestamp("start_date").notNull(),
  renewalDate: timestamp("renewal_date"),
  status: text("status").notNull().default("active"),
  assignedSeats: integer("assigned_seats").notNull().default(0),
  // productOwnerId references employees but employees is defined later in the file;
  // FK constraint is enforced at DB level via migration, not Drizzle reference.
  productOwnerId: integer("product_owner_id"),
});

// Subscription Plans table
export const subscriptionPlans = pgTable("subscription_plans", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  description: text("description"),
  price: doublePrecision("price"),
  currency: text("currency").notNull().default("USD"),
  billingCycle: text("billing_cycle"),
  features: jsonb("features"),
  maxUsers: integer("max_users"),
  maxApplications: integer("max_applications"),
  maxLicenses: integer("max_licenses"),
  isActive: boolean("is_active").notNull().default(true),
  stripePriceId: text("stripe_price_id"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  type: text("type"),
  monthlyPrice: integer("monthly_price").default(0),
  yearlyPrice: integer("yearly_price").default(0),
  stripeProductId: text("stripe_product_id"),
  stripeMonthlyPriceId: text("stripe_monthly_price_id"),
  stripeYearlyPriceId: text("stripe_yearly_price_id"),
});

// Tenant Subscriptions table
export const tenantSubscriptions = pgTable("tenant_subscriptions", {
  id: serial("id").primaryKey(),
  tenantId: integer("tenant_id").notNull().references(() => tenants.id, { onDelete: 'cascade' }),
  planId: integer("plan_id").references(() => subscriptionPlans.id),
  planType: text("plan_type").notNull().default("free"),
  status: text("status").notNull().default("active"),
  startDate: timestamp("start_date").defaultNow(),
  endDate: timestamp("end_date"),
  amount: integer("amount").default(0),
  currency: text("currency").default("USD"),
  stripeCustomerId: text("stripe_customer_id"),
  stripeSubscriptionId: text("stripe_subscription_id"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// Teams table
export const teams = pgTable("teams", {
  id: serial("id").primaryKey(),
  tenantId: integer("tenant_id").references(() => tenants.id, { onDelete: 'cascade' }),
  name: text("name").notNull(),
  description: text("description"),
  department: text("department"),
  manager: text("manager"),
  status: text("status").notNull().default("active"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  uniqueTeamNamePerTenant: unique("unique_team_name_per_tenant").on(table.tenantId, table.name),
}));

// Team Members table
export const teamMembers = pgTable("team_members", {
  id: serial("id").primaryKey(),
  teamId: integer("team_id").notNull().references(() => teams.id, { onDelete: 'cascade' }),
  employeeId: integer("employee_id").notNull().references(() => employees.id, { onDelete: 'cascade' }),
  role: text("role"),
  addedAt: timestamp("added_at").notNull().defaultNow(),
});

// Employees table
export const employees = pgTable("employees", {
  id: serial("id").primaryKey(),
  tenantId: integer("tenant_id").references(() => tenants.id, { onDelete: 'cascade' }),
  fullName: text("full_name").notNull(),
  email: text("email").notNull(),
  jobTitle: text("job_title"),
  department: text("department"),
  employeeId: text("employee_id"),
  status: text("status", { enum: employeeStatusEnum }).notNull().default("Active"),
  avatar: text("avatar"),
  addedAt: timestamp("added_at").notNull().defaultNow(),
}, (table) => ({
  uniqueEmployeeEmailPerTenant: unique("unique_employee_email_per_tenant").on(table.tenantId, table.email),
}));

// License Employees table
export const licenseEmployees = pgTable("license_employees", {
  id: serial("id").primaryKey(),
  licenseId: integer("license_id").notNull().references(() => licenses.id, { onDelete: 'cascade' }),
  employeeId: integer("employee_id").notNull().references(() => employees.id, { onDelete: 'cascade' }),
  assignedBy: integer("assigned_by").references(() => users.id, { onDelete: 'set null' }),
  assignedAt: timestamp("assigned_at").notNull().defaultNow(),
  expiresAt: timestamp("expires_at"),
  notes: text("notes"),
  status: text("status").notNull().default("active"),
});

// License Teams table
export const licenseTeams = pgTable("license_teams", {
  id: serial("id").primaryKey(),
  licenseId: integer("license_id").notNull().references(() => licenses.id, { onDelete: 'cascade' }),
  teamId: integer("team_id").notNull().references(() => teams.id, { onDelete: 'cascade' }),
  assignedBy: integer("assigned_by").references(() => users.id, { onDelete: 'set null' }),
  assignedAt: timestamp("assigned_at").notNull().defaultNow(),
  expiresAt: timestamp("expires_at"),
  notes: text("notes"),
  status: text("status").notNull().default("active"),
});

// User license assignments
export const userLicenses = pgTable("user_licenses", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: 'cascade' }),
  licenseId: integer("license_id").notNull().references(() => licenses.id, { onDelete: 'cascade' }),
  assignedAt: timestamp("assigned_at").notNull().defaultNow(),
  expiresAt: timestamp("expires_at"),
  status: text("status").notNull().default("active"),
});

// Usage logs
export const usageLogs = pgTable("usage_logs", {
  id: serial("id").primaryKey(),
  tenantId: integer("tenant_id").references(() => tenants.id, { onDelete: 'cascade' }),
  userId: integer("user_id").references(() => users.id, { onDelete: 'set null' }),
  applicationId: integer("application_id").references(() => applications.id, { onDelete: 'set null' }),
  action: text("action").notNull(),
  timestamp: timestamp("timestamp").notNull().defaultNow(),
  metadata: jsonb("metadata"),
});

// Activity logs
export const activityLogs = pgTable("activity_logs", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id, { onDelete: 'set null' }),
  tenantId: integer("tenant_id").references(() => tenants.id, { onDelete: 'cascade' }),
  type: text("type").notNull(),
  description: text("description").notNull(),
  timestamp: timestamp("timestamp").notNull().defaultNow(),
});

// Historical renewals
export const historicalRenewals = pgTable("historical_renewals", {
  id: serial("id").primaryKey(),
  licenseId: integer("license_id").notNull().references(() => licenses.id, { onDelete: 'cascade' }),
  renewalDate: timestamp("renewal_date"),
  previousCost: doublePrecision("previous_cost").notNull(),
  newCost: doublePrecision("new_cost").notNull(),
  currency: text("currency").notNull().default("USD"),
  notes: text("notes"),
  invoiceUrl: text("invoice_url"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

// Optimization recommendations
export const optimizationRecommendations = pgTable("optimization_recommendations", {
  id: serial("id").primaryKey(),
  tenantId: integer("tenant_id").references(() => tenants.id, { onDelete: 'cascade' }),
  type: text("type").notNull(),
  title: text("title").notNull(),
  description: text("description").notNull(),
  potentialSavings: doublePrecision("potential_savings"),
  priority: text("priority").notNull().default("medium"),
  status: text("status").notNull().default("pending"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

// Payment cards table
export const paymentCards = pgTable("payment_cards", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id, { onDelete: 'cascade' }),
  cardName: text("card_name").notNull(),
  cardType: text("card_type").notNull(),
  cardProvider: text("card_provider").notNull(),
  lastFourDigits: text("last_four_digits").notNull(),
  expiryMonth: integer("expiry_month").notNull(),
  expiryYear: integer("expiry_year").notNull(),
  cardholderName: text("cardholder_name").notNull(),
  isDefault: boolean("is_default").default(false),
  status: text("status").notNull().default("active"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  bankName: text("bank_name"),
  billingAddress: text("billing_address"),
});

// License Payment Cards
export const licensePaymentCards = pgTable("license_payment_cards", {
  id: serial("id").primaryKey(),
  licenseId: integer("license_id").notNull().references(() => licenses.id, { onDelete: 'cascade' }),
  paymentCardId: integer("payment_card_id").notNull().references(() => paymentCards.id, { onDelete: 'cascade' }),
  assignedAt: timestamp("assigned_at").notNull().defaultNow(),
  isActive: boolean("is_active").notNull().default(true),
});

// Blog Posts table
export const blogPosts = pgTable("blog_posts", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  slug: text("slug").notNull().unique(),
  excerpt: text("excerpt").notNull(),
  content: text("content").notNull(),
  authorName: text("author_name").notNull().default("SAASGAMA Team"),
  authorEmail: text("author_email").notNull().default("team@saasgama.com"),
  featuredImage: text("featured_image"),
  tags: text("tags").array(),
  isPublished: boolean("is_published").notNull().default(false),
  publishedAt: timestamp("published_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// Case Studies table
export const caseStudies = pgTable("case_studies", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  slug: text("slug").notNull().unique(),
  excerpt: text("excerpt").notNull(),
  content: text("content").notNull(),
  authorName: text("author_name").notNull().default("SAASGAMA Team"),
  authorEmail: text("author_email").notNull().default("team@saasgama.com"),
  featuredImage: text("featured_image"),
  tags: text("tags").array(),
  metaTitle: text("meta_title"),
  metaDescription: text("meta_description"),
  isPublished: boolean("is_published").notNull().default(false),
  publishedAt: timestamp("published_at"),
  views: integer("views").notNull().default(0),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// Contact Submissions table
export const contactSubmissions = pgTable("contact_submissions", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  email: text("email").notNull(),
  company: text("company"),
  phone: text("phone"),
  subject: text("subject").notNull(),
  message: text("message").notNull(),
  status: text("status").notNull().default("new"),
  adminNotes: text("admin_notes"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// Session table
export const session = pgTable("session", {
  sid: text("sid").primaryKey(),
  sess: jsonb("sess").notNull(),
  expire: timestamp("expire").notNull(),
});

// Currency Settings table
export const currencySettings = pgTable("currency_settings", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id, { onDelete: 'cascade' }),
  baseCurrency: text("base_currency").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// Email Campaigns table
export const emailCampaigns = pgTable("email_campaigns", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  status: text("status").notNull().default("draft"),
  type: text("type").notNull().default("one-time"),
  subject: text("subject").notNull(),
  body: text("body").notNull(),
  targetInfo: text("target_info"),
  sentCount: integer("sent_count").default(0),
  openCount: integer("open_count").default(0),
  clickCount: integer("click_count").default(0),
  createdAt: timestamp("created_at").defaultNow(),
  startDate: timestamp("start_date"),
  lastSentAt: timestamp("last_sent_at"),
  createdBy: integer("created_by").references(() => users.id, { onDelete: 'set null' }),
});

// Email Templates table
export const emailTemplates = pgTable("email_templates", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  type: text("type").notNull().default("notification"),
  subject: text("subject").notNull(),
  body: text("body").notNull(),
  description: text("description"),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  createdBy: integer("created_by").references(() => users.id, { onDelete: 'set null' }),
  startDate: timestamp("start_date"),
  endDate: timestamp("end_date"),
  triggerFrequency: text("trigger_frequency"),
  triggerInterval: integer("trigger_interval"),
  triggerConfig: jsonb("trigger_config"),
  targetCategory: text("target_category"),
  triggerLimitCount: integer("trigger_limit_count"),
});

// Email Tracking table
export const emailTracking = pgTable("email_tracking", {
  id: serial("id").primaryKey(),
  campaignId: integer("campaign_id").references(() => emailCampaigns.id, { onDelete: 'cascade' }),
  templateId: integer("template_id").references(() => emailTemplates.id, { onDelete: 'cascade' }),
  // Links a send back to the specific workflow run that created it, so the
  // "still not opened" filter can look at each workflow's own send history
  // instead of conflating sends from other workflows/campaigns that reuse
  // the same template.
  workflowId: integer("workflow_id").references(() => emailWorkflows.id, { onDelete: 'cascade' }),
  recipientEmail: text("recipient_email").notNull(),
  trackingKey: text("tracking_key").notNull().unique(),
  isOpened: boolean("is_opened").default(false).notNull(),
  openedAt: timestamp("opened_at"),
  // Whether ANY link in this send was clicked — mirrors isOpened/openedAt,
  // just for clicks. Powers the 'link_not_clicked' workflow trigger.
  isClicked: boolean("is_clicked").default(false).notNull(),
  clickedAt: timestamp("clicked_at"),
  sentAt: timestamp("sent_at").defaultNow(),
});

// Individual links inside a sent email, rewritten at send time to redirect
// through /api/track-click/:clickToken. Storing the real destination
// server-side (rather than accepting it as a client-supplied query param on
// the redirect route) avoids turning that route into an open redirect.
export const linkClicks = pgTable("link_clicks", {
  id: serial("id").primaryKey(),
  emailTrackingId: integer("email_tracking_id").notNull().references(() => emailTracking.id, { onDelete: 'cascade' }),
  clickToken: text("click_token").notNull().unique(),
  originalUrl: text("original_url").notNull(),
  clickedAt: timestamp("clicked_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

// Email Workflows table
export const emailWorkflows = pgTable("email_workflows", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  templateId: integer("template_id").references(() => emailTemplates.id, { onDelete: 'cascade' }),
  triggerAction: text("trigger_action").notNull(), // 'still_not_opened', 'email_in_spam', 'email_delivered', etc.
  status: text("status").notNull().default("draft"), // 'draft', 'active'
  frequency: text("frequency").notNull().default("once"), // 'once', 'daily', 'weekly', 'monthly'
  // How many days an email can go unopened before the 'still_not_opened' trigger resends it. Only meaningful for that trigger.
  notOpenedDays: integer("not_opened_days"),
  // Same idea as notOpenedDays but for the 'link_not_clicked' trigger.
  notClickedDays: integer("not_clicked_days"),
  // How many times within the chosen frequency's period (day/week/month) the workflow is allowed to fire. Only meaningful when frequency !== 'once'.
  frequencyCount: integer("frequency_count"),
  // The if/else condition: does the user have this tag? If elseTemplateId
  // is null, "no" just excludes them (original behavior). If it's set, "no"
  // routes them to the else-branch (different template/tag) instead of
  // being dropped from the send entirely.
  targetTagId: integer("target_tag_id").references(() => tags.id, { onDelete: 'set null' }),
  // Optional: automatically applies this tag to a user once they're actually sent this workflow's email — lets workflows be chained ("tag on send" -> a later workflow "targets that tag").
  applyTagId: integer("apply_tag_id").references(() => tags.id, { onDelete: 'set null' }),
  // Else-branch: only meaningful when targetTagId is set. Users WITHOUT
  // targetTagId get sent elseTemplateId (instead of templateId) and, if a
  // send succeeds, get tagged elseApplyTagId (instead of applyTagId).
  elseTemplateId: integer("else_template_id").references(() => emailTemplates.id, { onDelete: 'set null' }),
  elseApplyTagId: integer("else_apply_tag_id").references(() => tags.id, { onDelete: 'set null' }),
  lastTriggeredAt: timestamp("last_triggered_at"),
  nodes: jsonb("nodes"),
  edges: jsonb("edges"),
  // 'legacy' = the fields above (single trigger + one if/else condition),
  // handled by processActiveWorkflows(). 'graph' = an arbitrary node chain
  // in workflowNodes/workflowEdges below, handled by processGraphWorkflows()
  // and tracked per-subscriber in workflowSubscriberState. Kept as two
  // separate engines rather than migrating legacy workflows so existing
  // production automations are never touched by the new code path.
  engineType: text("engine_type").notNull().default("legacy"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// ─── Graph automation engine (ConvertKit-style) ─────────────────────────────
// A workflow with engineType='graph' is a chain of these nodes connected by
// workflowEdges, walked per-subscriber by processGraphWorkflows(). Unlike the
// legacy engine (one shot per tick, no memory of where a user "is"),
// subscribers persist their current node across ticks via
// workflowSubscriberState — that's what makes a real "wait 3 days, then
// check a condition" sequence possible instead of only a single branch.
export const workflowNodes = pgTable("workflow_nodes", {
  id: serial("id").primaryKey(),
  workflowId: integer("workflow_id").notNull().references(() => emailWorkflows.id, { onDelete: 'cascade' }),
  // 'trigger' (exactly one per graph, the entry point) | 'condition' (tag
  // check, branches yes/no via workflowEdges) | 'action' (send_email /
  // add_tags / remove_tags / wait) | 'exit' (terminal, ends the run).
  type: text("type").notNull(),
  // Shape depends on `type`:
  //   trigger:   { triggerAction, templateId, notOpenedDays?, notClickedDays? }
  //   condition: { tagIds: number[], matchMode: 'any' | 'all' }
  //   action:    { actionType: 'send_email'|'add_tags'|'remove_tags'|'wait', templateId?, tagIds?: number[], waitDays?: number }
  //   exit:      {}
  config: jsonb("config").notNull().default({}),
  positionX: integer("position_x").notNull().default(0),
  positionY: integer("position_y").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const workflowEdges = pgTable("workflow_edges", {
  id: serial("id").primaryKey(),
  workflowId: integer("workflow_id").notNull().references(() => emailWorkflows.id, { onDelete: 'cascade' }),
  fromNodeId: integer("from_node_id").notNull().references(() => workflowNodes.id, { onDelete: 'cascade' }),
  toNodeId: integer("to_node_id").notNull().references(() => workflowNodes.id, { onDelete: 'cascade' }),
  // 'none' for a plain unconditional edge (trigger->x, action->x). 'yes'/'no'
  // for the two edges leaving a condition node. Deliberately NOT NULL: a SQL
  // UNIQUE constraint treats every NULL as distinct from every other NULL,
  // so "UNIQUE(from_node_id, branch)" would never actually have caught two
  // unconditional edges leaving the same node if this were nullable.
  branch: text("branch").notNull().default("none"),
}, (table) => ({
  uniqueFromBranch: unique().on(table.fromNodeId, table.branch),
}));

// One row per (workflow, user) — where that specific person currently sits
// in the graph. This is the piece that makes multi-step automations actually
// stateful: a 'wait' action node checks enteredNodeAt against real elapsed
// time on every tick until it's satisfied, however many ticks that takes.
export const workflowSubscriberState = pgTable("workflow_subscriber_state", {
  id: serial("id").primaryKey(),
  workflowId: integer("workflow_id").notNull().references(() => emailWorkflows.id, { onDelete: 'cascade' }),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: 'cascade' }),
  currentNodeId: integer("current_node_id").references(() => workflowNodes.id, { onDelete: 'set null' }),
  status: text("status").notNull().default("active"), // 'active' | 'completed'
  enteredNodeAt: timestamp("entered_node_at").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => ({
  // A user runs through a given automation at most once — no re-entry once
  // they have a row here, active or completed. Matches how ConvertKit
  // automations behave by default (not a loop).
  uniqueWorkflowUser: unique().on(table.workflowId, table.userId),
}));

// Tags — free-form labels admins can apply to users (email recipients) for
// segmentation, independent of the fixed trial/expired/expiring/operational
// target categories. Modeled after ConvertKit's tag system: a join table
// (userTags) rather than an array column on `users`, so a tag can be
// renamed in one place and "who has tag X" stays an indexed lookup instead
// of an array-contains scan across every user.
export const tags = pgTable("tags", {
  id: serial("id").primaryKey(),
  name: text("name").notNull().unique(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const userTags = pgTable("user_tags", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull().references(() => users.id, { onDelete: 'cascade' }),
  tagId: integer("tag_id").notNull().references(() => tags.id, { onDelete: 'cascade' }),
  // Denormalized copy of the user's email, captured automatically from the
  // users table at the moment the tag is applied (manual or workflow) —
  // never typed by an admin. Lets a tag's audience be read straight off this
  // row without a join, and keeps a record of who was tagged even if their
  // account email later changes.
  email: text("email").notNull(),
  appliedAt: timestamp("applied_at").defaultNow(),
  // How the tag was applied: 'manual' (admin action), 'click'/'open' (email
  // engagement tracking), 'webhook' (e.g. a Stripe event), 'form'. Only
  // 'manual' is wired up so far — the rest are reserved for the automatic
  // tagging triggers planned as a follow-up phase.
  source: text("source").notNull().default("manual"),
}, (table) => ({
  uniqueUserTag: unique().on(table.userId, table.tagId),
}));

// Exchange Rates table
export const exchangeRates = pgTable("exchange_rates", {
  id: serial("id").primaryKey(),
  fromCurrency: text("from_currency").notNull(),
  toCurrency: text("to_currency").notNull(),
  rate: doublePrecision("rate").notNull(),
  date: timestamp("date").notNull().defaultNow(),
});

// App Settings table
export const appSettings = pgTable("app_settings", {
  id: serial("id").primaryKey(),
  type: text("type").notNull(),
  value: text("value").notNull(),
  tenantId: integer("tenant_id").references(() => tenants.id, { onDelete: 'cascade' }),
  displayOrder: integer("display_order").notNull().default(0),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Email Settings table
export const emailSettings = pgTable("email_settings", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id, { onDelete: 'cascade' }),
  provider: text("provider").notNull().default("mailgun"),
  apiKey: text("api_key").notNull(),
  fromEmail: text("from_email").notNull(),
  fromName: text("from_name").notNull(),
  domain: text("domain"),
  region: text("region"),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Notifications table
export const notifications = pgTable("notifications", {
  id: serial("id").primaryKey(),
  tenantId: integer("tenantId").references(() => tenants.id, { onDelete: 'cascade' }).notNull(),
  userId: integer("userId").references(() => users.id, { onDelete: 'cascade' }),
  title: text("title").notNull(),
  message: text("message").notNull(),
  type: text("type").notNull().default("general"), // general, warning, error, success, license, subscription, application, etc.
  priority: text("priority").notNull().default("normal"), // low, normal, high, urgent
  isRead: boolean("is_read").notNull().default(false),
  actionUrl: text("action_url"), // Optional URL for click actions
  metadata: jsonb("metadata"), // Additional data (license IDs, application IDs, etc.)
  expiresAt: timestamp("expires_at"), // Optional expiration for temporary notifications
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// User Invitations table - for inviting secondary users
export const userInvitations = pgTable("user_invitations", {
  id: serial("id").primaryKey(),
  tenantId: integer("tenant_id").references(() => tenants.id, { onDelete: 'cascade' }).notNull(),
  invitedByUserId: integer("invited_by_user_id").references(() => users.id, { onDelete: 'cascade' }).notNull(),
  email: text("email").notNull(),
  role: text("role").notNull().default("member"), // member, editor, viewer
  invitationToken: text("invitation_token").notNull().unique(),
  status: text("status").notNull().default("pending"), // pending, accepted, expired, cancelled
  permissions: jsonb("permissions"), // Custom permissions JSON
  expiresAt: timestamp("expires_at").notNull(),
  acceptedAt: timestamp("accepted_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// Email Change Requests table - self-service request, admin approval required
export const emailChangeRequests = pgTable("email_change_requests", {
  id: serial("id").primaryKey(),
  tenantId: integer("tenant_id").references(() => tenants.id, { onDelete: 'cascade' }).notNull(),
  userId: integer("user_id").references(() => users.id, { onDelete: 'cascade' }).notNull(),
  currentEmail: text("current_email").notNull(),
  requestedEmail: text("requested_email").notNull(),
  status: text("status").notNull().default("pending"), // pending, approved, rejected
  requestedAt: timestamp("requested_at").notNull().defaultNow(),
  resolvedAt: timestamp("resolved_at"),
  resolvedByUserId: integer("resolved_by_user_id").references(() => users.id, { onDelete: 'set null' }),
  rejectionReason: text("rejection_reason"),
});

// User Permissions table - granular permissions for secondary users
export const userPermissions = pgTable("user_permissions", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id, { onDelete: 'cascade' }).notNull(),
  tenantId: integer("tenant_id").references(() => tenants.id, { onDelete: 'cascade' }).notNull(),
  resource: text("resource").notNull(), // dashboard, applications, licenses, teams, analytics, settings, billing
  action: text("action").notNull(), // view, create, edit, delete, manage
  scope: text("scope").default("all"), // all, assigned, own
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (table) => ({
  uniquePermission: unique("unique_user_resource_action").on(table.userId, table.resource, table.action),
}));

// User Activity Tracking table - track all user actions for admin oversight
export const userActivity = pgTable("user_activity", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").references(() => users.id, { onDelete: 'cascade' }).notNull(),
  tenantId: integer("tenant_id").references(() => tenants.id, { onDelete: 'cascade' }).notNull(),
  action: text("action").notNull(), // login, logout, create_application, edit_license, etc.
  resource: text("resource"), // applications, licenses, teams, users, etc.
  resourceId: integer("resource_id"), // ID of the affected resource
  details: jsonb("details"), // Additional context (before/after values, etc.)
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

// Import Sessions table - track bulk data imports for admin-controlled bulk deletion
export const importSessions = pgTable("import_sessions", {
  id: serial("id").primaryKey(),
  sessionId: text("session_id").notNull().unique(), // UUID for this import session
  tenantId: integer("tenant_id").references(() => tenants.id, { onDelete: 'cascade' }).notNull(),
  userId: integer("user_id").references(() => users.id, { onDelete: 'set null' }),
  fileName: text("file_name").notNull(), // Original CSV filename
  importType: text("import_type").notNull(), // applications, licenses, employees, teams, or unified
  totalRecords: integer("total_records").notNull(), // Total records attempted
  successfulRecords: integer("successful_records").notNull(), // Successfully created records
  failedRecords: integer("failed_records").notNull(), // Failed records
  createdRecords: jsonb("created_records").notNull(), // Array of {type: string, ids: number[]} for bulk deletion
  errorLog: jsonb("error_log"), // Detailed error information
  status: text("status").notNull().default("completed"), // completed, failed, partial
  cooldownExpiresAt: timestamp("cooldown_expires_at").notNull(), // When bulk delete option expires (24-48 hours)
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

// Insert Schemas
export const insertTenantSchema = createInsertSchema(tenants).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});

export const insertUserSchema = createInsertSchema(users).omit({
  id: true,
});

export const insertApplicationSchema = createInsertSchema(applications).omit({
  id: true,
  addedAt: true,
}).extend({
  name: z.string()
    .trim()
    .min(1, "Application name is required")
    .max(100, "Application name must be less than 100 characters")
    .refine((name) => name.replace(/\s+/g, '').length > 0, {
      message: "Application name cannot be only whitespace"
    }),
  category: z.string().trim().min(1, "Category is required").max(50, "Category must be less than 50 characters"),
  description: z.string().optional(),
  tenantId: z.number().min(1, "Tenant ID is required"),
});

export const insertLicenseSchema = createInsertSchema(licenses).omit({
  id: true,
}).extend({
  applicationId: z.number().min(1, "Please select an application"),
  name: z.string().trim().min(1, "License name is required").max(100, "License name must be less than 100 characters"),
  totalSeats: z.number().min(1, "Total seats must be at least 1"),
  cost: z.number().min(0, "Cost cannot be negative"),
  currency: z.string().min(1, "Currency is required"),
  billingCycle: z.string().min(1, "Billing cycle is required"),
}).refine((data) => {
  // Validation for status vs renewal date logic
  if (data.status === 'expired' && data.renewalDate) {
    const now = new Date();
    const renewalDate = new Date(data.renewalDate);
    // If status is 'expired', renewal date should be in the past
    if (renewalDate > now) {
      return false;
    }
  }
  if (data.status === 'active' && data.renewalDate) {
    const now = new Date();
    const renewalDate = new Date(data.renewalDate);
    // If renewal date is in the past and billing is not lifetime, should not be active
    if (renewalDate < now && !['Lifetime', 'One-Time'].includes(data.billingCycle)) {
      return false;
    }
  }
  return true;
}, {
  message: "License status must be consistent with renewal date. Expired licenses cannot have future renewal dates, and non-lifetime licenses with past renewal dates should not be active.",
});

// Helper function to calculate license status based on renewal date
export const calculateLicenseStatus = (renewalDate: Date, billingCycle: string): string => {
  // Lifetime and One-Time licenses never expire
  if (['Lifetime', 'One-Time'].includes(billingCycle)) {
    return 'active';
  }
  
  const now = new Date();
  if (renewalDate < now) {
    return 'expired';
  } else {
    return 'active';
  }
};

export const insertSubscriptionPlanSchema = createInsertSchema(subscriptionPlans).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});

export const insertTenantSubscriptionSchema = createInsertSchema(tenantSubscriptions).omit({
  id: true,
  createdAt: true,
  updatedAt: true
});

export const insertTeamSchema = createInsertSchema(teams).omit({
  id: true,
  createdAt: true,
});

export const insertTeamMemberSchema = createInsertSchema(teamMembers).omit({
  id: true,
  addedAt: true,
});

export const insertEmployeeSchema = createInsertSchema(employees).omit({
  id: true,
  addedAt: true,
});

export const insertLicenseEmployeeSchema = createInsertSchema(licenseEmployees).omit({
  id: true,
  assignedAt: true,
});

export const insertLicenseTeamSchema = createInsertSchema(licenseTeams).omit({
  id: true,
  assignedAt: true,
});

export const insertUserLicenseSchema = createInsertSchema(userLicenses).omit({
  id: true,
  assignedAt: true,
});

export const insertUsageLogSchema = createInsertSchema(usageLogs).omit({
  id: true,
  timestamp: true,
});

export const insertActivityLogSchema = createInsertSchema(activityLogs).omit({
  id: true,
  timestamp: true,
});

export const insertHistoricalRenewalSchema = createInsertSchema(historicalRenewals).omit({
  id: true,
  createdAt: true,
});

export const insertOptimizationRecommendationSchema = createInsertSchema(optimizationRecommendations).omit({
  id: true,
  createdAt: true,
});

export const insertPaymentCardSchema = createInsertSchema(paymentCards).omit({
  id: true,
  createdAt: true,
});

export const insertLicensePaymentCardSchema = createInsertSchema(licensePaymentCards).omit({
  id: true,
  assignedAt: true,
});

export const insertBlogPostSchema = createInsertSchema(blogPosts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertCaseStudySchema = z.object({
  title: z.string().min(1, "Title is required"),
  slug: z.string().optional(),
  excerpt: z.string().min(1, "Excerpt is required"),
  content: z.string().min(1, "Content is required"),
  authorName: z.string().default("SAASGAMA Team"),
  authorEmail: z.string().email().default("team@saasgama.com"),
  featuredImage: z.string().optional(),
  isPublished: z.boolean().default(false),
  tags: z.array(z.string()).optional(),
  metaTitle: z.string().optional(),
  metaDescription: z.string().optional(),
});

export const insertContactSubmissionSchema = createInsertSchema(contactSubmissions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertCurrencySettingsSchema = createInsertSchema(currencySettings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertEmailCampaignSchema = createInsertSchema(emailCampaigns).omit({
  id: true,
  createdAt: true,
});

export const insertEmailTemplateSchema = createInsertSchema(emailTemplates).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertEmailTrackingSchema = createInsertSchema(emailTracking).omit({
  id: true,
  sentAt: true,
});

export const insertExchangeRateSchema = createInsertSchema(exchangeRates).omit({
  id: true,
  date: true,
});

export const insertAppSettingSchema = createInsertSchema(appSettings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertEmailSettingSchema = createInsertSchema(emailSettings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertEmailOtpVerificationSchema = createInsertSchema(emailOtpVerification).omit({
  id: true,
  createdAt: true,
});

export const insertNotificationSchema = createInsertSchema(notifications).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Types
export type Tenant = typeof tenants.$inferSelect;
export type InsertTenant = z.infer<typeof insertTenantSchema>;

export type User = typeof users.$inferSelect;
export type InsertUser = z.infer<typeof insertUserSchema>;

export type Application = typeof applications.$inferSelect;
export type InsertApplication = z.infer<typeof insertApplicationSchema>;

export type License = typeof licenses.$inferSelect;
export type InsertLicense = z.infer<typeof insertLicenseSchema>;
export type LicenseWithOwner = License & { productOwnerName?: string | null };

export type SubscriptionPlan = typeof subscriptionPlans.$inferSelect;
export type InsertSubscriptionPlan = z.infer<typeof insertSubscriptionPlanSchema>;

export type TenantSubscription = typeof tenantSubscriptions.$inferSelect;
export type InsertTenantSubscription = z.infer<typeof insertTenantSubscriptionSchema>;

export type Team = typeof teams.$inferSelect;
export type InsertTeam = z.infer<typeof insertTeamSchema>;

export type TeamMember = typeof teamMembers.$inferSelect;
export type InsertTeamMember = z.infer<typeof insertTeamMemberSchema>;

export type Employee = typeof employees.$inferSelect;
export type InsertEmployee = z.infer<typeof insertEmployeeSchema>;

export type LicenseEmployee = typeof licenseEmployees.$inferSelect;
export type InsertLicenseEmployee = z.infer<typeof insertLicenseEmployeeSchema>;

export type LicenseTeam = typeof licenseTeams.$inferSelect;
export type InsertLicenseTeam = z.infer<typeof insertLicenseTeamSchema>;

export type UserLicense = typeof userLicenses.$inferSelect;
export type InsertUserLicense = z.infer<typeof insertUserLicenseSchema>;

export type UsageLog = typeof usageLogs.$inferSelect;
export type InsertUsageLog = z.infer<typeof insertUsageLogSchema>;

export type ActivityLog = typeof activityLogs.$inferSelect;
export type InsertActivityLog = z.infer<typeof insertActivityLogSchema>;

export type HistoricalRenewal = typeof historicalRenewals.$inferSelect;
export type InsertHistoricalRenewal = z.infer<typeof insertHistoricalRenewalSchema>;

export type OptimizationRecommendation = typeof optimizationRecommendations.$inferSelect;
export type InsertOptimizationRecommendation = z.infer<typeof insertOptimizationRecommendationSchema>;

export type PaymentCard = typeof paymentCards.$inferSelect;
export type InsertPaymentCard = z.infer<typeof insertPaymentCardSchema>;

export type LicensePaymentCard = typeof licensePaymentCards.$inferSelect;
export type InsertLicensePaymentCard = z.infer<typeof insertLicensePaymentCardSchema>;

export type BlogPost = typeof blogPosts.$inferSelect;
export type InsertBlogPost = z.infer<typeof insertBlogPostSchema>;

export type CaseStudy = typeof caseStudies.$inferSelect;
export type InsertCaseStudy = z.infer<typeof insertCaseStudySchema>;

export type ContactSubmission = typeof contactSubmissions.$inferSelect;
export type InsertContactSubmission = z.infer<typeof insertContactSubmissionSchema>;

export type Session = typeof session.$inferSelect;

export type CurrencySettings = typeof currencySettings.$inferSelect;
export type InsertCurrencySettings = z.infer<typeof insertCurrencySettingsSchema>;

export type EmailCampaign = typeof emailCampaigns.$inferSelect;
export type InsertEmailCampaign = z.infer<typeof insertEmailCampaignSchema>;

export type EmailTemplate = typeof emailTemplates.$inferSelect;
export type InsertEmailTemplate = z.infer<typeof insertEmailTemplateSchema>;

export type ExchangeRate = typeof exchangeRates.$inferSelect;
export type InsertExchangeRate = z.infer<typeof insertExchangeRateSchema>;

export type AppSetting = typeof appSettings.$inferSelect;
export type InsertAppSetting = z.infer<typeof insertAppSettingSchema>;

export type EmailSetting = typeof emailSettings.$inferSelect;
export type InsertEmailSetting = z.infer<typeof insertEmailSettingSchema>;

export type EmailOtpVerification = typeof emailOtpVerification.$inferSelect;
export type InsertEmailOtpVerification = z.infer<typeof insertEmailOtpVerificationSchema>;

export type Notification = typeof notifications.$inferSelect;
export type InsertNotification = z.infer<typeof insertNotificationSchema>;

// User management schemas and types
export const insertUserInvitationSchema = createInsertSchema(userInvitations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertEmailChangeRequestSchema = createInsertSchema(emailChangeRequests).omit({
  id: true,
  requestedAt: true,
  resolvedAt: true,
  resolvedByUserId: true,
  status: true,
  rejectionReason: true,
});

export type EmailChangeRequest = typeof emailChangeRequests.$inferSelect;
export type InsertEmailChangeRequest = z.infer<typeof insertEmailChangeRequestSchema>;

export const insertUserPermissionSchema = createInsertSchema(userPermissions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertUserActivitySchema = createInsertSchema(userActivity).omit({
  id: true,
  createdAt: true,
});

export const insertImportSessionSchema = createInsertSchema(importSessions).omit({
  id: true,
  createdAt: true,
});

export type UserInvitation = typeof userInvitations.$inferSelect;
export type InsertUserInvitation = z.infer<typeof insertUserInvitationSchema>;

export type UserPermission = typeof userPermissions.$inferSelect;
export type InsertUserPermission = z.infer<typeof insertUserPermissionSchema>;

export type UserActivity = typeof userActivity.$inferSelect;
export type InsertUserActivity = z.infer<typeof insertUserActivitySchema>;

export type ImportSession = typeof importSessions.$inferSelect;
export type InsertImportSession = z.infer<typeof insertImportSessionSchema>;

// System Settings table for maintenance mode and global settings
export const systemSettings = pgTable("system_settings", {
  id: serial("id").primaryKey(),
  key: text("key").notNull().unique(),
  value: jsonb("value").notNull(),
  description: text("description"),
  updatedBy: integer("updated_by").references(() => users.id),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertSystemSettingsSchema = createInsertSchema(systemSettings).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type SystemSettings = typeof systemSettings.$inferSelect;
export type InsertSystemSettings = z.infer<typeof insertSystemSettingsSchema>;

// Dashboard Stats Types
export type DashboardStats = {
  totalApplications: number;
  activeLicenses: number;
  totalMonthlyCost: number;
  optimizationPotential: number;
  todayRenewals: number;
  todayRenewalsCost: number;
  weekRenewals: number;
  weekRenewalsCost: number;
  applicationsGrowth: number;
  licensesGrowth: number;
  missedRenewals: number;
  missedRenewalsCost: number;
};

export const dashboardStatsSchema = z.object({
  totalApplications: z.number().default(0),
  activeLicenses: z.number().default(0),
  totalMonthlyCost: z.number().default(0),
  optimizationPotential: z.number().default(0),
  todayRenewals: z.number().default(0),
  todayRenewalsCost: z.number().default(0),
  weekRenewals: z.number().default(0),
  weekRenewalsCost: z.number().default(0),
  applicationsGrowth: z.number().default(0),
  licensesGrowth: z.number().default(0),
  missedRenewals: z.number().default(0),
  missedRenewalsCost: z.number().default(0),
});

export type EmailTracking = typeof emailTracking.$inferSelect;
export type InsertEmailTracking = z.infer<typeof insertEmailTrackingSchema>;

export const insertEmailWorkflowSchema = createInsertSchema(emailWorkflows).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type EmailWorkflow = typeof emailWorkflows.$inferSelect;
export type InsertEmailWorkflow = z.infer<typeof insertEmailWorkflowSchema>;
